import React, { useRef, useState } from 'react';

import { Botao, Field, FormActions, FormSection, Input, Nota, RadioCards } from '../../ui';
import Icone from '../../ui/primitivos/Icone';

import ModalRegionais, { Regional } from '../modal/regionais/ModalRegionais';
import ModalCooperativas from '../modal/cooperativas/ModalCooperativas';
import { Cooperativa } from '../../api/sga-cooperativa/sga-cooperativaApi';

import { PapelUsuario, UsuarioDetalhado } from '../../api/user/userApi';
import { formatCpfCnpj } from '../../utils/formartCPF';
import { formatTelefone } from '../../utils/mascaras';
import { REGRA_SENHA, gerarSenhaForte, senhaForte } from '../../utils/senha';
import { papeisOferecidos, volunteerCodeObrigatorio } from '../../utils/usuarioRegras';
import { validarCpfCnpj, validarEmail, validarObrigatorio, validarTelefone } from '../../utils/validacaoAssociado';

/**
 * O formulário de usuário — **um componente para criar e editar** (at-68, frente D).
 *
 * ## Por que existe
 *
 * Medido em 11/09/2026: `criarUsuario.tsx` (369 linhas) e o modo-edição de
 * `usuarios.tsx` (575) declaravam **o mesmo formulário** — os mesmos campos, os mesmos
 * dois modais de vínculo, a mesma validação. E já tinham **divergido em três regras**:
 * a criação filtrava o código de voluntário a dígitos com mínimo de 6 (que recusaria o
 * único código real do banco, `918`), oferecia a opção ADMIN sem checar quem estava
 * logado, e voltava para o hub em vez da lista. É a definição de "regra repetida em
 * componente é divergência com data marcada" (at-24).
 *
 * ⚠️ **Mora em `components/` e não em `src/ui`**: conhece o domínio (a API de usuário,
 * os modais do SGA, as regras de papel). O que ele usa do kit é puro — precedente do
 * `FormularioAssociado` (at-58) e do `FormularioVeiculo` (at-59).
 *
 * ## O que mudou em relação às duas telas antigas
 *
 *   - **Erro junto do campo** (`role="alert"`), com rolagem até o primeiro. Antes eram
 *     `toast.error` um de cada vez, mais um botão desabilitado que não dizia o que
 *     faltava — o estado que a spec 03 §4 chama de pior possível no celular.
 *   - **Papel em `RadioCards`**, com a descrição do que cada um faz, em vez de um
 *     `<select>` de três linhas sem contexto.
 *   - **Código de voluntário: só dígitos, sem mínimo de tamanho.** O backend exige
 *     apenas não-vazio, e os códigos do SGA são curtos (918, 100, 200).
 *   - **Uma regra de senha só** (`utils/senha.ts`), com o gerador que existia apenas no
 *     modal órfão apagado na at-67 — e que gerava senhas com caracteres que o DTO do
 *     backend recusa.
 */

export interface DadosUsuarioForm {
  nomeCompleto: string;
  email: string;
  cpfCnpj: string;
  celular: string;
  volunteerCode: string;
  userType: PapelUsuario;
  codigo_regionais: string[];
  codigo_cooperativas: string[];
  /** Só no modo criar. */
  password?: string;
  confirmPassword?: string;
}

interface VinculoChip {
  codigo: string;
  nome: string;
}

export interface FormularioUsuarioProps {
  modo: 'criar' | 'editar';
  valoresIniciais?: UsuarioDetalhado | null;
  /** Papel de quem está mexendo — decide quais papéis são oferecidos. */
  requesterUserType?: string | null;
  /**
   * No modo editar recebe **só o que mudou** (o diff); no criar, tudo.
   * ⚠️ O diff é o que impede um 400 em campo legado que o TI não tocou: usuários
   * importados por CSV podem ter celular fora do formato que a DTO aceita.
   */
  aoSalvar: (dados: Partial<DadosUsuarioForm>) => void;
  salvando?: boolean;
  desabilitado?: boolean;
  rotuloAcao?: string;
}

type CampoForm =
  | 'nomeCompleto'
  | 'email'
  | 'cpfCnpj'
  | 'celular'
  | 'volunteerCode'
  | 'password'
  | 'confirmPassword';

type Erros = Partial<Record<CampoForm, string>>;

const somenteDigitos = (valor: string) => valor.replace(/\D/g, '');

const CLASSE_CHIP_VINCULO =
  'tw-inline-flex tw-items-center tw-gap-2 tw-rounded-full tw-border tw-border-superficie-borda tw-bg-superficie tw-px-3 tw-py-1 tw-text-sm tw-text-texto-corpo';

/* Alvo de toque de 28px com o ícone do kit — o padrão do "remover" da at-60. */
const CLASSE_REMOVER_VINCULO =
  'tw-flex tw-h-7 tw-w-7 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-bg-transparent tw-text-texto-suave hover:tw-bg-danger-bg hover:tw-text-danger disabled:tw-cursor-not-allowed disabled:tw-opacity-50';

const FormularioUsuario: React.FC<FormularioUsuarioProps> = ({
  modo,
  valoresIniciais,
  requesterUserType,
  aoSalvar,
  salvando = false,
  desabilitado = false,
  rotuloAcao,
}) => {
  const formRef = useRef<HTMLFormElement>(null);

  /*
   * ⚠️ Lidos UMA vez, no inicializador do estado. Um `useEffect` que sincronizasse
   * apagaria o que o TI está digitando quando a ficha recarrega — a lição das at-58/59.
   */
  const [form, setForm] = useState(() => ({
    nomeCompleto: valoresIniciais?.nomeCompleto ?? '',
    email: valoresIniciais?.email ?? '',
    cpfCnpj: valoresIniciais?.cpfCnpj ? formatCpfCnpj(valoresIniciais.cpfCnpj) : '',
    celular: valoresIniciais?.celular ? formatTelefone(valoresIniciais.celular) : '',
    volunteerCode: valoresIniciais?.volunteerCode ?? '',
    userType: (valoresIniciais?.userType ?? 'CONSULTOR') as PapelUsuario,
    password: '',
    confirmPassword: '',
  }));

  const [regionais, setRegionais] = useState<VinculoChip[]>(() =>
    (valoresIniciais?.regionais ?? []).map(r => ({
      codigo: r.codigo_regional ?? '',
      nome: r.nome,
    })),
  );
  const [cooperativas, setCooperativas] = useState<VinculoChip[]>(() =>
    (valoresIniciais?.cooperativas ?? []).map(c => ({
      codigo: c.codigo_cooperativa ?? '',
      nome: c.nome,
    })),
  );

  const [erros, setErros] = useState<Erros>({});
  const [modalRegionais, setModalRegionais] = useState(false);
  const [modalCooperativas, setModalCooperativas] = useState(false);
  const [senhaGerada, setSenhaGerada] = useState('');

  const papeis = papeisOferecidos(requesterUserType, valoresIniciais?.userType);
  const codigoObrigatorio = volunteerCodeObrigatorio(form.userType);

  const alterar = (campo: keyof typeof form, valor: string) => {
    setForm(antes => ({ ...antes, [campo]: valor }));
    setErros(antes => ({ ...antes, [campo]: undefined }));
  };

  const validar = (): Erros => {
    const e: Erros = {};

    e.nomeCompleto = validarObrigatorio(form.nomeCompleto) ?? undefined;
    e.email = validarEmail(form.email) ?? undefined;
    e.cpfCnpj = validarCpfCnpj(form.cpfCnpj) ?? undefined;
    e.celular = validarTelefone(form.celular, true) ?? undefined;

    if (codigoObrigatorio && !somenteDigitos(form.volunteerCode)) {
      e.volunteerCode = 'Informe o código de voluntário';
    }

    if (modo === 'criar') {
      if (!form.password) {
        e.password = 'Defina uma senha inicial';
      } else if (!senhaForte(form.password)) {
        e.password = REGRA_SENHA;
      }

      if (form.password && form.password !== form.confirmPassword) {
        e.confirmPassword = 'As senhas não coincidem';
      }
    }

    (Object.keys(e) as CampoForm[]).forEach(k => {
      if (!e[k]) delete e[k];
    });

    return e;
  };

  /**
   * No modo editar manda **só o que mudou**. Reenviar um campo intocado derruba a
   * edição inteira com 400 quando o valor legado não passa no `@Matches` da DTO —
   * caso real de quem veio da importação por CSV.
   */
  const montarDiff = (): Partial<DadosUsuarioForm> => {
    const original = valoresIniciais;
    const diff: Partial<DadosUsuarioForm> = {};

    const compararTexto = (
      campo: 'nomeCompleto' | 'email' | 'cpfCnpj' | 'celular' | 'volunteerCode',
      atual: string,
      anterior: string | null | undefined,
    ) => {
      if (atual !== (anterior ?? '')) diff[campo] = atual;
    };

    compararTexto('nomeCompleto', form.nomeCompleto.trim(), original?.nomeCompleto);
    compararTexto('email', form.email.trim(), original?.email);
    compararTexto('cpfCnpj', somenteDigitos(form.cpfCnpj), original?.cpfCnpj ? somenteDigitos(original.cpfCnpj) : '');
    compararTexto('celular', somenteDigitos(form.celular), original?.celular ? somenteDigitos(original.celular) : '');
    compararTexto('volunteerCode', somenteDigitos(form.volunteerCode), original?.volunteerCode);

    if (form.userType !== original?.userType) diff.userType = form.userType;

    const mesmoConjunto = (a: string[], b: string[]) =>
      JSON.stringify([...a].sort()) === JSON.stringify([...b].sort());

    const codigosRegionais = regionais.map(r => r.codigo);
    const codigosCooperativas = cooperativas.map(c => c.codigo);
    const originaisRegionais = (original?.regionais ?? []).map(r => r.codigo_regional ?? '');
    const originaisCooperativas = (original?.cooperativas ?? []).map(c => c.codigo_cooperativa ?? '');

    if (!mesmoConjunto(codigosRegionais, originaisRegionais)) {
      diff.codigo_regionais = codigosRegionais;
    }
    if (!mesmoConjunto(codigosCooperativas, originaisCooperativas)) {
      diff.codigo_cooperativas = codigosCooperativas;
    }

    return diff;
  };

  const enviar = (evento: React.FormEvent) => {
    evento.preventDefault();

    const encontrados = validar();
    if (Object.keys(encontrados).length > 0) {
      setErros(encontrados);

      const primeiro = Object.keys(encontrados)[0];
      const alvo = formRef.current?.querySelector(`[data-field="${primeiro}"]`);

      // Defensivo: `scrollIntoView` não existe no jsdom nem em toda WebView antiga, e
      // uma exceção aqui abortaria o submit deixando o clique sem reação visível.
      if (typeof (alvo as any)?.scrollIntoView === 'function') {
        (alvo as any).scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      return;
    }

    if (modo === 'editar') {
      aoSalvar(montarDiff());
      return;
    }

    aoSalvar({
      nomeCompleto: form.nomeCompleto.trim(),
      email: form.email.trim(),
      cpfCnpj: somenteDigitos(form.cpfCnpj),
      celular: somenteDigitos(form.celular),
      volunteerCode: somenteDigitos(form.volunteerCode),
      userType: form.userType,
      codigo_regionais: regionais.map(r => r.codigo),
      codigo_cooperativas: cooperativas.map(c => c.codigo),
      password: form.password,
      confirmPassword: form.confirmPassword,
    });
  };

  const codigoAlterado =
    modo === 'editar' &&
    !!valoresIniciais &&
    somenteDigitos(form.volunteerCode) !== valoresIniciais.volunteerCode;

  const gerar = () => {
    const nova = gerarSenhaForte();
    setForm(antes => ({ ...antes, password: nova, confirmPassword: nova }));
    setErros(antes => ({ ...antes, password: undefined, confirmPassword: undefined }));
    setSenhaGerada(nova);
  };

  const removerRegional = (codigo: string) =>
    setRegionais(antes => antes.filter(r => r.codigo !== codigo));
  const removerCooperativa = (codigo: string) =>
    setCooperativas(antes => antes.filter(c => c.codigo !== codigo));

  return (
    <form ref={formRef} onSubmit={enviar} noValidate className="tw-flex tw-flex-col tw-gap-8">
      <fieldset disabled={desabilitado} className="tw-m-0 tw-flex tw-flex-col tw-gap-8 tw-border-0 tw-p-0">
        <FormSection titulo="Identificação" icone="identificacao">
          <div className="tw-grid tw-gap-4 sm:tw-grid-cols-2">
            <Field
              rotulo="Nome completo"
              obrigatorio
              erro={erros.nomeCompleto}
              campo="nomeCompleto"
              className="sm:tw-col-span-2"
            >
              <Input
                value={form.nomeCompleto}
                onChange={e => alterar('nomeCompleto', e.target.value)}
                placeholder="Nome como aparece nos documentos"
                autoComplete="off"
              />
            </Field>

            <Field rotulo="CPF / CNPJ" obrigatorio erro={erros.cpfCnpj} campo="cpfCnpj">
              <Input
                value={form.cpfCnpj}
                onChange={e => alterar('cpfCnpj', formatCpfCnpj(e.target.value))}
                inputMode="numeric"
                placeholder="000.000.000-00"
                autoComplete="off"
              />
            </Field>

            <Field
              rotulo="Código de voluntário"
              obrigatorio={codigoObrigatorio}
              erro={erros.volunteerCode}
              campo="volunteerCode"
              dica="É por ele que os associados ficam ligados ao consultor."
            >
              <Input
                value={form.volunteerCode}
                onChange={e => alterar('volunteerCode', somenteDigitos(e.target.value))}
                inputMode="numeric"
                placeholder="918"
                autoComplete="off"
              />
            </Field>
          </div>

          {codigoAlterado && (
            <Nota variante="atencao">
              Trocar o código de voluntário <strong>não atualiza os cadastros que já foram enviados
              ao SGA</strong> com o código antigo. Os associados deste usuário no SAAC continuam
              vinculados a ele.
            </Nota>
          )}
        </FormSection>

        <FormSection titulo="Contato" icone="contato">
          <div className="tw-grid tw-gap-4 sm:tw-grid-cols-2">
            <Field rotulo="E-mail" obrigatorio erro={erros.email} campo="email" dica="É com ele que o usuário faz login.">
              <Input
                type="email"
                value={form.email}
                onChange={e => alterar('email', e.target.value)}
                placeholder="nome@atualclube.org"
                autoComplete="off"
              />
            </Field>

            <Field rotulo="Celular" obrigatorio erro={erros.celular} campo="celular">
              <Input
                value={form.celular}
                onChange={e => alterar('celular', formatTelefone(e.target.value))}
                inputMode="numeric"
                placeholder="(31) 99999-9999"
                autoComplete="off"
              />
            </Field>
          </div>
        </FormSection>

        <FormSection titulo="Papel no sistema" icone="pessoa">
          {/*
            A descrição do papel ESCOLHIDO vai na dica do campo, não dentro de cada
            opção: o `RadioCards` é uma linha de alvos curtos, e três cartões com
            parágrafo dentro não cabem no celular. Assim o TI escolhe e lê o que
            aquilo significa, sem mudar o primitivo do kit.
          */}
          <Field
            rotulo="Papel"
            obrigatorio
            grupo
            campo="userType"
            dica={papeis.find(p => p.valor === form.userType)?.descricao}
          >
            <RadioCards
              nome="userType"
              valor={form.userType}
              aoMudar={valor => alterar('userType', valor)}
              opcoes={papeis.map(p => ({ valor: p.valor, rotulo: p.rotulo }))}
            />
          </Field>

          {papeis.every(p => p.valor !== 'ADMIN') && (
            <Nota>
              A opção <strong>Administrador</strong> só aparece para quem já é
              administrador.
            </Nota>
          )}
        </FormSection>

        <FormSection titulo="Vínculos" icone="local">
          <Nota>
            Regionais e cooperativas definem <strong>quais cotas e produtos</strong> o usuário
            enxerga nas telas de cotação e adesão.
          </Nota>

          <div className="tw-flex tw-flex-col tw-gap-4">
            <div className="tw-flex tw-flex-col tw-gap-2">
              <div className="tw-flex tw-items-center tw-justify-between tw-gap-3">
                <span className="tw-text-sm tw-font-semibold tw-text-texto-titulo">
                  Regionais ({regionais.length})
                </span>
                <Botao
                  type="button"
                  variante="secundario"
                  tamanho="sm"
                  iconeEsquerda="mais"
                  onClick={() => setModalRegionais(true)}
                >
                  Adicionar regional
                </Botao>
              </div>

              {regionais.length === 0 ? (
                <p className="tw-m-0 tw-text-sm tw-text-texto-suave">Nenhuma regional vinculada.</p>
              ) : (
                <ul className="tw-m-0 tw-flex tw-list-none tw-flex-wrap tw-gap-2 tw-p-0">
                  {regionais.map(r => (
                    <li key={r.codigo}>
                      <span className={CLASSE_CHIP_VINCULO}>
                        {r.nome} ({r.codigo})
                        <button
                          type="button"
                          onClick={() => removerRegional(r.codigo)}
                          disabled={desabilitado}
                          aria-label={`Remover a regional ${r.nome}`}
                          className={CLASSE_REMOVER_VINCULO}
                        >
                          <Icone nome="fechar" tamanho={14} />
                        </button>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="tw-flex tw-flex-col tw-gap-2">
              <div className="tw-flex tw-items-center tw-justify-between tw-gap-3">
                <span className="tw-text-sm tw-font-semibold tw-text-texto-titulo">
                  Cooperativas ({cooperativas.length})
                </span>
                <Botao
                  type="button"
                  variante="secundario"
                  tamanho="sm"
                  iconeEsquerda="mais"
                  onClick={() => setModalCooperativas(true)}
                >
                  Adicionar cooperativa
                </Botao>
              </div>

              {cooperativas.length === 0 ? (
                <p className="tw-m-0 tw-text-sm tw-text-texto-suave">
                  Nenhuma cooperativa vinculada.
                </p>
              ) : (
                <ul className="tw-m-0 tw-flex tw-list-none tw-flex-wrap tw-gap-2 tw-p-0">
                  {cooperativas.map(c => (
                    <li key={c.codigo}>
                      <span className={CLASSE_CHIP_VINCULO}>
                        {c.nome} ({c.codigo})
                        <button
                          type="button"
                          onClick={() => removerCooperativa(c.codigo)}
                          disabled={desabilitado}
                          aria-label={`Remover a cooperativa ${c.nome}`}
                          className={CLASSE_REMOVER_VINCULO}
                        >
                          <Icone nome="fechar" tamanho={14} />
                        </button>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </FormSection>

        {modo === 'criar' && (
          <FormSection titulo="Senha inicial" icone="check">
            <Nota>
              O usuário <strong>troca esta senha no primeiro acesso</strong>. Anote e entregue a ele
              por um canal seguro.
            </Nota>

            <div className="tw-grid tw-gap-4 sm:tw-grid-cols-2">
              <Field rotulo="Senha" obrigatorio erro={erros.password} campo="password" dica={REGRA_SENHA}>
                <Input
                  type="password"
                  value={form.password}
                  onChange={e => alterar('password', e.target.value)}
                  autoComplete="new-password"
                />
              </Field>

              <Field
                rotulo="Confirmar senha"
                obrigatorio
                erro={erros.confirmPassword}
                campo="confirmPassword"
              >
                <Input
                  type="password"
                  value={form.confirmPassword}
                  onChange={e => alterar('confirmPassword', e.target.value)}
                  autoComplete="new-password"
                />
              </Field>
            </div>

            <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3">
              <Botao type="button" variante="fantasma" tamanho="sm" onClick={gerar}>
                Gerar senha forte
              </Botao>

              {senhaGerada && (
                <span className="tw-text-sm tw-text-texto-corpo">
                  Senha gerada: <strong className="tw-font-mono">{senhaGerada}</strong>
                </span>
              )}
            </div>
          </FormSection>
        )}

        <FormActions>
          <Botao
            type="submit"
            tamanho="lg"
            carregando={salvando}
            larguraTotal
            className="sm:tw-w-auto sm:tw-min-w-[220px]"
          >
            {rotuloAcao ?? (modo === 'criar' ? 'Criar usuário' : 'Salvar alterações')}
          </Botao>
        </FormActions>
      </fieldset>

      <ModalRegionais
        isOpen={modalRegionais}
        onClose={() => setModalRegionais(false)}
        selectedCodigos={regionais.map(r => r.codigo)}
        onSelectRegional={(regional: Regional) => {
          setRegionais(antes =>
            antes.some(r => r.codigo === regional.codigo_regional)
              ? antes
              : [...antes, { codigo: regional.codigo_regional, nome: regional.nome }],
          );
          setModalRegionais(false);
        }}
      />

      <ModalCooperativas
        isOpen={modalCooperativas}
        onClose={() => setModalCooperativas(false)}
        selectedCodigos={cooperativas.map(c => c.codigo)}
        onSelectCooperativa={(cooperativa: Cooperativa) => {
          setCooperativas(antes =>
            antes.some(c => c.codigo === cooperativa.codigo_cooperativa)
              ? antes
              : [...antes, { codigo: cooperativa.codigo_cooperativa, nome: cooperativa.nome }],
          );
          setModalCooperativas(false);
        }}
      />
    </form>
  );
};

export default FormularioUsuario;
